import { ViewConfig } from '../../../models/view-config.interface';
import { CarrierDeliveryNotesPaginatedService } from '@app/carrier/services/carrier-delivery-notes-paginated.service';
import { AppStore } from '@app/stores/appStore';
import { CarrierViewActionsService } from '../carrier-view-actions.service';
import { TableActionsConfig } from '@app/shared/components/table-cell-components/table-actions.component';
import { ColumnCreator } from '../column-creator';
import { FilterKeyCreator } from '../filter-key-creator';
import {
    DeliveryNoteData,
    TableRowData,
    isDeliveryNoteData,
    hasDeliveryNoteBundleStatus,
    hasReadOnly,
    hasDateReceived,
} from '@app/shared/models/table-data-types';
import { TourPlanStore } from '@app/stores/tourPlanStore';

export interface CarrierDeliveryNotesConfigDeps {
    carrierDeliveryNotesPaginatedService: CarrierDeliveryNotesPaginatedService;
    actionsService: CarrierViewActionsService;
    appStore: InstanceType<typeof AppStore>;
    tourPlanStore: InstanceType<typeof TourPlanStore>;
}

export class CarrierDeliveryNotesConfig {
    static create(deps: CarrierDeliveryNotesConfigDeps): ViewConfig {
        return {
            id: 'carrier-deliverynotes',
            name: 'Delivery Notes',
            translationKey: 'OUTGOING-GOODS.TOGGLER.DELIVERY-NOTES',
            type: 'deliverynotes',
            userRole: 'carrier',
            apiConfig: {
                loadData: () => {
                    const orgSiteKey =
                        deps.appStore.currentOrganizationSiteKey();
                    if (!orgSiteKey) {
                        return Promise.resolve([]);
                    }
                    return deps.carrierDeliveryNotesPaginatedService.loadCarrierDeliveryNotesPaginated(
                        orgSiteKey
                    );
                },
            },
            tableConfig: {
                columns: [
                    ColumnCreator.select({
                        visible: context => deps.appStore.isTourPlanMode(),
                        selectableCondition: (item: TableRowData) => {
                            // Read signal to trigger OnPush change detection when tour plan changes
                            deps.tourPlanStore.tourPlanVersion();
                            // Check if this delivery note is already in tour planning
                            if (!isDeliveryNoteData(item)) return false;
                            const tourPlanNotes =
                                deps.tourPlanStore.tourPlanDeliveryNotes();
                            const isInTour = tourPlanNotes.some(
                                tourNote => tourNote._key === item._key
                            );
                            return !isInTour; // Only selectable if NOT in tour
                        },
                    }),
                    ColumnCreator.deliveryNoteNumber({
                        header: 'SPEDITEUR.TABLE.HEADERS.NOTE-NUMBER',
                    }),
                    ColumnCreator.actions({
                        width: '150px',
                        configuration: {
                            type: 'deliveryNote',
                            showView: true,
                            showDownload: true,

                            // Endpoints
                            viewEndpoint: (item: TableRowData) => {
                                if (isDeliveryNoteData(item)) {
                                    deps.actionsService.viewDeliveryNote(
                                        item as DeliveryNoteData
                                    );
                                }
                            },
                            downloadEndpoint: (item: TableRowData) => {
                                if (isDeliveryNoteData(item)) {
                                    deps.actionsService.downloadDeliveryNote(
                                        item as DeliveryNoteData
                                    );
                                }
                            },

                            // Configuration
                            context: 'carrier',

                            // Visibility conditions - carriers can view/download all delivery notes
                            isEditVisible: () => false,
                            isDeleteVisible: () => false,
                        } as TableActionsConfig,
                    }),
                    ColumnCreator.orderNumber(),
                    ColumnCreator.referenceNumber({
                        orgType: 'carrier',
                        readOnly: row => {
                            const isClosed =
                                hasDeliveryNoteBundleStatus(row) &&
                                row.deliveryNoteBundleStatus === 'closed';
                            const isReadOnly =
                                hasReadOnly(row) && row.read_only;
                            const hasReceivedDate =
                                hasDateReceived(row) && !!row.dateReceived;
                            return !!(
                                isClosed ||
                                isReadOnly ||
                                hasReceivedDate
                            );
                        },
                        organizationSiteKeyResolver: row =>
                            (row as DeliveryNoteData).carrierKey || '',
                    }),
                    ColumnCreator.deliveryNoteBundleNumber({
                        header: 'SPEDITEUR.TABLE.HEADERS.BUNDLE-KEY',
                    }),
                    ColumnCreator.tourNumber(),
                    ColumnCreator.originalConsignor(),
                    ColumnCreator.originalConsignorGln(),
                    ColumnCreator.consignorNameWithExchange(),
                    ColumnCreator.carrierName({
                        header: 'INCOMING-GOODS.TABLE.CARRIER-NAME',
                    }),
                    ColumnCreator.consigneeName(),
                    ColumnCreator.dateShipped({
                        header: 'SPEDITEUR.TABLE.HEADERS.SENT-DATE',
                    }),
                    ColumnCreator.dateReceived(),
                    ColumnCreator.carrierLicensePlate({
                        plateType: 'outgoing',
                    }),
                    ColumnCreator.carrierLicensePlate({
                        plateType: 'incoming',
                    }),
                    ColumnCreator.dnStatus(),
                    ColumnCreator.discrepancies(),
                ],
                features: {
                    sortable: true,
                    filterable: true,
                    paginated: true,
                    selectable: true,
                    expandable: false,
                },
            },
            filterConfig: {
                filterKeys: [
                    FilterKeyCreator.text(
                        'number',
                        'SPEDITEUR.TABLE.HEADERS.NOTE-NUMBER'
                    ),
                    FilterKeyCreator.text(
                        'orderNumber',
                        'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ORDER-NO'
                    ),
                    FilterKeyCreator.text(
                        'referenceNumber',
                        'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.REFERENCE-NUMBER',
                        'properties.referenceNumber'
                    ),
                    FilterKeyCreator.text(
                        'deliveryNoteBundleNumber',
                        'SPEDITEUR.TABLE.HEADERS.BUNDLE-KEY'
                    ),
                    FilterKeyCreator.text(
                        'tourNumber',
                        'SPEDITEUR.TABLE.HEADERS.TOUR-KEY'
                    ),
                    FilterKeyCreator.text(
                        'originalConsignor',
                        'GLOBAL.ORIGINAL_CONSIGNOR'
                    ),
                    FilterKeyCreator.text(
                        'originalConsignorGln',
                        'GLOBAL.GLN'
                    ),
                    FilterKeyCreator.text(
                        'consignorName',
                        'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CONSIGNOR-NAME'
                    ),
                    FilterKeyCreator.text(
                        'carrierName',
                        'INCOMING-GOODS.TABLE.CARRIER-NAME'
                    ),
                    FilterKeyCreator.text(
                        'consigneeName',
                        'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CONSIGNEE-NAME'
                    ),
                    FilterKeyCreator.date(
                        'checkoutDate',
                        'SPEDITEUR.TABLE.HEADERS.SENT-DATE'
                    ),
                    FilterKeyCreator.date(
                        'checkinDate',
                        'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.RECEIVE-DATE'
                    ),
                    FilterKeyCreator.text(
                        'carrierNumberPlateOutgoing',
                        'SPEDITEUR.TABLE.HEADERS.PLATE-IN'
                    ),
                    FilterKeyCreator.text(
                        'carrierNumberPlateIncoming',
                        'SPEDITEUR.TABLE.HEADERS.PLATE-OUT'
                    ),
                    FilterKeyCreator.status(
                        'status',
                        'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DELIVERY-NOTE-STATUS'
                    ),
                    FilterKeyCreator.boolean(
                        'discrepancies',
                        'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DISCREPANCIES'
                    ),
                ],
                showClosed: true,
                enableAdvancedFilters: true,
            },
            searchConfig: {
                validSortFields: [
                    'dateCreated',
                    'number',
                    'orderNumber',
                    'consignorName',
                    'consigneeName',
                    'carrierName',
                    'deliveryNoteBundleNumber',
                    'dateShipped',
                    'dateAnnounced',
                    'dateReceived',
                    'status',
                ],
                defaultSortField: 'dateCreated',
                defaultSortDirection: 'desc',
            },
            headerActionConfig: {
                headerActions: [
                    {
                        id: 'sharePermissions',
                        label: 'Share Permissions',
                        translationKey:
                            'SPEDITEUR.TABLE.ACTIONS.SHARE-PERMISSIONS',
                        icon: 'ios_share',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 xl:!tw-text-xl',
                        position: 'right',
                        order: 1,
                        visible: (item, selection) =>
                            !deps.appStore.isTourPlanMode(),
                        // Fernzugriff ist nur lesend — ohne die Site-Berechtigung bleibt die Aktion gesperrt.
                        disabled: (item, selection) =>
                            !deps.appStore.currentSiteRole()?.siteInfo
                                ?.permissions?.carrier_delivery_notes_share_rights,
                        handler: async (item, selection) => {
                            const organizationSite =
                                deps.appStore.currentOrganizationSite();
                            if (organizationSite) {
                                await deps.actionsService.sharePermissions(
                                    selection || [],
                                    organizationSite
                                );
                            }
                        },
                    },
                    {
                        id: 'planTour',
                        label: 'Plan Tour',
                        translationKey: 'SPEDITEUR.TABLE.ACTIONS.PLAN-TOUR',
                        icon: 'qr_code_scanner',
                        color: 'primary',
                        variant: 'raised',
                        cssClasses:
                            'fix-button-position tw-h-14 xl:!tw-text-xl',
                        position: 'right',
                        order: 2,
                        visible: (item, selection) =>
                            !deps.appStore.isTourPlanMode(),
                        // Fernzugriff ist nur lesend — ohne die Site-Berechtigung bleibt die Aktion gesperrt.
                        disabled: (item, selection) =>
                            !deps.appStore.currentSiteRole()?.siteInfo
                                ?.permissions?.carrier_tours_create,
                        handler: async (item, selection) => {
                            await deps.actionsService.planTour(selection || []);
                        },
                    },
                ],
            },
            permissions: {
                canCreate: true,
                canUpdate: true,
                canDelete: true,
                canExport: true,
                canImport: false,
            },
            selectionConfig: {
                clearOnViewTypeSwitch: true,
            },
        };
    }
}
